import React from "react";
import { Card, Table, Button } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";

const currencyFormat = new Intl.NumberFormat("en-CA", {
   style: "currency",
   currency: "CAD",
   minimumFractionDigits: 2,
   maximumFractionDigits: 2,
   currencyDisplay: "narrowSymbol",
});

const toNumber = (value) => {
   const n = Number(value);
   return Number.isFinite(n) ? n : 0;
};

const formatPercent = (value) => {
   if (typeof value === "string") return value;
   const n = toNumber(value);
   return `${n.toFixed(1)}%`;
};

const SkeletonTopSuppliers = () => (
   <Card className="card-one" style={{ minHeight: "340px" }}>
      <Card.Header>
         <div className="placeholder-glow" style={{ width: "70%" }}>
            <span className="placeholder col-9 d-block" style={{ height: "16px" }}></span>
         </div>
      </Card.Header>
      <Card.Body className="placeholder-glow">
         <Table className="table-one" responsive>
            <thead>
               <tr>
                  <th><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></th>
                  <th><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></th>
                  <th><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></th>
                  <th><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></th>
                  <th><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></th>
               </tr>
            </thead>
            <tbody>
               {Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                     <td><span className="placeholder col-10 d-block" style={{ height: "12px" }}></span></td>
                     <td><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></td>
                     <td><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></td>
                     <td><span className="placeholder col-8 d-block" style={{ height: "12px" }}></span></td>
                     <td><span className="placeholder col-7 d-block" style={{ height: "12px" }}></span></td>
                  </tr>
               ))}
            </tbody>
         </Table>
      </Card.Body>
   </Card>
);

const ErrorTopSuppliers = ({ onRetry }) => (
   <Card className="card-one" style={{ minHeight: "340px" }}>
      <Card.Header>
         <Card.Title as="h6">Top Suppliers Reconciliation</Card.Title>
      </Card.Header>
      <Card.Body className="d-flex flex-column align-items-center justify-content-center">
         <p className="text-muted mb-3">Failed to load supplier reconciliation</p>
         {onRetry && (
            <Button variant="primary" size="sm" onClick={onRetry}>
               Retry
            </Button>
         )}
      </Card.Body>
   </Card>
);


const fetchTopSuppliers = async () => {
   const res = await fetch("/getkpisuppliers.sz", {
      method: "POST",
      credentials: "include",
   });
   if (!res.ok) throw new Error("Failed to fetch supplier reconciliation");
   const json = await res.json();
   if (json.st !== 1) throw new Error(json.mg || "API error");
   return json.payload;
};

const TopSuppliersReconciliation = () => {
   const {
      data = [],
      isLoading,
      isError,
      refetch,
   } = useQuery({
      queryKey: ["top-suppliers"],
      queryFn: fetchTopSuppliers,
      staleTime: 5 * 60 * 1000,
   });

   if (isLoading) return <SkeletonTopSuppliers />;
   if (isError) return <ErrorTopSuppliers onRetry={refetch} />;

   return (
      <Card className="card-one">
         <Card.Header>
            <Card.Title as="h6">Current Month Top Suppliers Reconciliation</Card.Title>
         </Card.Header>
         <Card.Body>
            <Table className="table-one" responsive>
               <thead>
                  <tr>
                     <th>Supplier</th>
                     <th className="text-end">Total</th>
                     <th className="text-end">Reconciled</th>
                     <th className="text-end">Outstanding</th>
                     <th className="text-end">Reconciliation %</th>
                     <th className="text-end">Invoices</th>
                     <th className="text-end">Follow Ups</th>
                  </tr>
               </thead>
               <tbody>
                  {data.length > 0 ? (
                     data.map((item, index) => (
                        <tr key={`${item.supplier_id}-${index}`}>
                           <td>
                              <div className="d-flex align-items-center fw-medium">{item.supplier_name}</div>
                           </td>
                           <td className="text-end">{currencyFormat.format(toNumber(item.statement_total))}</td>
                           <td className="text-end">{currencyFormat.format(toNumber(item.reconciled_amount))}</td>
                           <td className="text-end">{currencyFormat.format(toNumber(item.outstanding_difference))}</td>
                           <td className="text-end">{formatPercent(item.percent_difference)}</td>
                           <td className="text-end">{toNumber(item.erp_invoice_count)}</td>
                           <td className="text-end">{toNumber(item.followup_count)}</td>
                        </tr>
                     ))
                  ) : (
                     <tr>
                        <td colSpan={7} className="text-center text-muted">No supplier data available.</td>
                     </tr>
                  )}
               </tbody>
            </Table>
         </Card.Body>
      </Card>
   );
};

export default TopSuppliersReconciliation;
